import { motion } from "framer-motion";
import { Rocket, Globe2, Blocks, Landmark } from "lucide-react";

const PHASES = [
  { icon: Rocket, phase: "Phase 01", title: "Windows vault — live now", body: "The desktop core: cold-key generation, portfolio command, atomic swaps." },
  { icon: Blocks, phase: "Phase 02", title: "Every chain, one ledger", body: "Unified balances across all major networks with native, bridge-free swaps." },
  { icon: Globe2, phase: "Phase 03", title: "Cyrion everywhere", body: "macOS, Linux and mobile — one vault, synced through your enclave, never our servers." },
  { icon: Landmark, phase: "Phase 04", title: "The all-in-one wallet", body: "Staking, fiat rails, and institutional-grade reporting inside a single sovereign app." },
];

export const About = () => {
  return (
    <section id="vision" className="py-24 px-4 sm:px-6 lg:px-8 border-t border-white/5 relative overflow-hidden" data-testid="about-section">
      <div className="absolute -bottom-32 left-1/4 w-[600px] h-[400px] rounded-full bg-gold/[0.05] blur-[130px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-mint mb-4">About us</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-100 mb-8" data-testid="about-heading">
            We are building the wallet <span className="gold-text">crypto was promised.</span>
          </h2>
          <div className="space-y-5 text-base sm:text-lg text-slate-400 leading-relaxed" data-testid="about-copy">
            <p>
              Cyrion began with a simple frustration: serious holders juggle five tools — one for keys,
              one for swaps, one for staking, one for taxes, one for sleep lost over all of them.
            </p>
            <p>
              We are a small, obsessed team of cryptographers and desktop engineers with an ambitious plan:
              forge the best all-in-one wallet in the industry — a single sovereign command center where you
              hold, swap, stake, and report without ever surrendering your keys or your data.
            </p>
            <p className="text-slate-300 font-medium">
              The Windows vault is chapter one. Everything on the roadmap is already in the forge.
            </p>
          </div>
        </motion.div>

        <div className="relative">
          <div className="absolute left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-gold/40 via-white/10 to-transparent" />
          <div className="space-y-8">
            {PHASES.map((p, i) => (
              <motion.div
                key={p.phase}
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.12 }}
                className="relative pl-16"
                data-testid={`roadmap-phase-${i + 1}`}
              >
                <div className="absolute left-0 top-0 w-11 h-11 rounded-xl bg-obsidian border border-gold/30 flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.15)]">
                  <p.icon className="w-5 h-5 text-gold" />
                </div>
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-mint mb-1">{p.phase}</p>
                <h3 className="font-display text-lg font-semibold text-slate-100 mb-1">{p.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
